/** Keep modal focus contained, lock background scrolling, and restore its initiating control. */
import { type RefObject, useEffect, useRef } from 'react'

let scrollLocks = 0
let priorDocumentOverflow = ''
let priorBodyOverflow = ''
let priorDocumentOverscroll = ''

function lockDocumentScroll() {
  if (scrollLocks++ > 0) return
  const root = document.documentElement
  priorDocumentOverflow = root.style.overflow
  priorBodyOverflow = document.body.style.overflow
  priorDocumentOverscroll = root.style.overscrollBehavior
  root.style.overflow = 'hidden'
  root.style.overscrollBehavior = 'none'
  document.body.style.overflow = 'hidden'
  document.body.dataset.modalOpen = 'true'
}

function unlockDocumentScroll() {
  if (--scrollLocks > 0) return
  scrollLocks = 0
  const root = document.documentElement
  root.style.overflow = priorDocumentOverflow
  root.style.overscrollBehavior = priorDocumentOverscroll
  document.body.style.overflow = priorBodyOverflow
  delete document.body.dataset.modalOpen
}

export function useDialogFocus(
  root: RefObject<HTMLElement | null>,
  onClose: () => void,
  canClose = true,
  returnFocus?: HTMLElement | null,
  enabled = true,
) {
  const close = useRef(onClose),
    allowed = useRef(canClose)
  close.current = onClose
  allowed.current = canClose
  useEffect(() => {
    if (!enabled) return
    lockDocumentScroll()
    const previous =
      returnFocus ??
      (document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null)
    const controls = () =>
      Array.from(
        root.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]',
        ) ?? [],
      ).filter(
        (element) =>
          element.getClientRects().length > 0 && !element.closest('[inert]'),
      )
    const focus = () => (controls()[0] ?? root.current)?.focus()
    focus()
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && event.target instanceof Element) {
        // Menus close themselves, and a dialog nested inside this one owns
        // its own Escape: closing both at once would lose the outer work.
        if (event.target.closest('[role="listbox"], [role="menu"]')) return
        const nested = event.target.closest('[role="dialog"]')
        if (nested && nested !== root.current && root.current?.contains(nested))
          return
      }
      if (event.key === 'Escape') {
        event.stopPropagation()
        event.preventDefault()
        if (allowed.current) close.current()
      }
      if (event.key === 'Tab') {
        const items = controls(),
          at = items.indexOf(document.activeElement as HTMLElement)
        if (!items.length) {
          event.preventDefault()
          root.current?.focus()
        } else if (
          at < 0 ||
          (event.shiftKey && at === 0) ||
          (!event.shiftKey && at === items.length - 1)
        ) {
          event.preventDefault()
          items[event.shiftKey ? items.length - 1 : 0]?.focus()
        }
      }
    }
    const contain = (event: FocusEvent) => {
      if (!root.current?.contains(event.target as Node)) focus()
    }
    document.addEventListener('keydown', key, true)
    document.addEventListener('focusin', contain)
    return () => {
      unlockDocumentScroll()
      document.removeEventListener('keydown', key, true)
      document.removeEventListener('focusin', contain)
      if (previous?.isConnected) previous.focus()
    }
  }, [root, returnFocus, enabled])
}
